/**
 * Admin-Approve dialog — Access `frmReportExposureQryLimit_Approved`.
 * Loads rows awaiting approval (is_approved=0) into an editable grid with a tick
 * per row + Tick All (default all ticked). Save UPDATEs each ticked row →
 * is_approved=1 with the edited quantity / amount / note (limit_date is shown but
 * not updated, mirroring Access btnSave). Visible only to approver accounts.
 *
 * Grid columns 1:1 Access `frmReportExposureQryLimit_ApprovedQry`: tick · cp_code
 * (readonly) · short_name (Name, readonly) · limit_date (display) · quantity_limit_mt
 * (EDIT, Standard) · amount_limit_usd (EDIT, Standard) · limit_note (EDIT).
 * btnSave/btnCancel from the Access FormHeader → DialogTitle (right).
 */
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import DialogTitleBar from '../../../components/DialogTitleBar'
import { useEffect, useMemo, useState } from 'react'
import { useSnackbar } from '../../../hooks/useSnackbar'
import {
  useApproveExposureLimitsMutation,
  useGetExposurePendingQuery,
  type ExposureLimit,
} from '../../../store/api/dashboardApi'

interface Props {
  open: boolean
  onClose: () => void
  onSaved: () => void
}

interface EditRow {
  id: number
  cpCode: string
  shortName: string
  limitDate: string | null
  quantity: string
  amount: string
  note: string
  checked: boolean
}

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const formatDate = (value: string | null): string => {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}
const numericInputProps = { style: { textAlign: 'right' as const }, inputMode: 'decimal' as const }
function parseNumeric(v: string): number | null {
  const s = (v ?? '').toString().replace(/,/g, '').trim()
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

// Exposure Limit MT / USD display with 0 decimals (chị Đẹp).
function formatStandard(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return ''
  const n = typeof v === 'number' ? v : parseNumeric(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

const toEditRow = (r: ExposureLimit): EditRow => ({
  id: r.id as number,
  cpCode: r.code ?? '',
  shortName: r.shortName ?? r.name ?? '',
  limitDate: r.limitDate,
  quantity: formatStandard(r.quantityLimitMt),
  amount: formatStandard(r.amountLimitUsd),
  note: r.limitNote ?? '',
  checked: true,
})

export default function ApproveExposureDialog({ open, onClose, onSaved }: Props) {
  const { data: pending, isFetching } = useGetExposurePendingQuery(undefined, {
    skip: !open,
    refetchOnMountOrArgChange: true,
  })
  const [approve, { isLoading: saving }] = useApproveExposureLimitsMutation()
  const [rows, setRows] = useState<EditRow[]>([])
  const snack = useSnackbar()

  useEffect(() => {
    if (open && pending) setRows(pending.filter((r) => r.id != null).map(toEditRow))
  }, [open, pending])

  const allChecked = rows.length > 0 && rows.every((r) => r.checked)
  const updateRow = (id: number, patch: Partial<EditRow>) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)))
  const toggleAll = (checked: boolean) => setRows((prev) => prev.map((r) => ({ ...r, checked })))

  const tickedCount = useMemo(() => rows.filter((r) => r.checked).length, [rows])

  const handleClose = () => {
    if (saving) return
    onClose()
  }

  const handleSave = async () => {
    if (saving) return
    const items = rows
      .filter((r) => r.checked)
      .map((r) => ({
        id: r.id,
        quantityLimitMt: parseNumeric(r.quantity),
        amountLimitUsd: parseNumeric(r.amount),
        limitNote: r.note || null,
      }))
    if (items.length === 0) {
      snack.error('Please tick at least one row to approve.')
      return
    }
    try {
      await approve({ items }).unwrap()
      snack.success('Successfully Updated')
      onSaved()
      onClose()
    } catch (err: unknown) {
      const detail = (err as { data?: { detail?: string } })?.data?.detail
      snack.error(detail ?? 'Could not approve exposure limits.')
    }
  }

  const cellSx = { py: 0.25, whiteSpace: 'nowrap' as const }

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (saving || reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitleBar
        title={
          <>
            Approve Exposure Limit{' '}
            <Typography component="span" variant="caption" color="text.secondary">
              (Only approve Ticked Rows)
            </Typography>
          </>
        }
        onSave={handleSave}
        onCancel={handleClose}
        saving={saving}
        saveDisabled={saving || tickedCount === 0}
        saveLabel={`Save (${tickedCount})`}
      />
      <DialogContent dividers>
        {isFetching && rows.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 8 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Box>
        ) : rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No exposure limits awaiting approval.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allChecked}
                    indeterminate={!allChecked && rows.some((r) => r.checked)}
                    onChange={(e) => toggleAll(e.target.checked)}
                  />
                </TableCell>
                <TableCell>CP Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Limit Date</TableCell>
                <TableCell align="right">Exposure Limit MT (EDIT)</TableCell>
                <TableCell align="right">Exposure Limit USD (EDIT)</TableCell>
                <TableCell>Note (EDIT)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={r.checked}
                      onChange={(e) => updateRow(r.id, { checked: e.target.checked })}
                    />
                  </TableCell>
                  <TableCell sx={cellSx}>{r.cpCode}</TableCell>
                  <TableCell sx={cellSx}>{r.shortName}</TableCell>
                  <TableCell sx={cellSx}>{formatDate(r.limitDate)}</TableCell>
                  <TableCell sx={cellSx} align="right">
                    <TextField
                      value={r.quantity}
                      onChange={(e) => updateRow(r.id, { quantity: e.target.value })}
                      onBlur={() => updateRow(r.id, { quantity: formatStandard(r.quantity) })}
                      size="small"
                      type="text"
                      variant="standard"
                      inputProps={numericInputProps}
                    />
                  </TableCell>
                  <TableCell sx={cellSx} align="right">
                    <TextField
                      value={r.amount}
                      onChange={(e) => updateRow(r.id, { amount: e.target.value })}
                      onBlur={() => updateRow(r.id, { amount: formatStandard(r.amount) })}
                      size="small"
                      type="text"
                      variant="standard"
                      inputProps={numericInputProps}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.25, minWidth: 160 }}>
                    <TextField
                      value={r.note}
                      onChange={(e) => updateRow(r.id, { note: e.target.value })}
                      size="small"
                      variant="standard"
                      fullWidth
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  )
}
